import React, { useState, useEffect, Fragment } from 'react';
import GlobalContext from './GlobalContext';

import { useIdleTimer } from "react-idle-timer";

/*
    This context provider provides access to any variables which are likely to be needed throughout the application, such
    as the logged in user type and the customer ID

    You can use it in any React component by doing the following:

    import GlobalContext from "../../../contexts/GlobalContext";

    const contextValue = useContext(GlobalContext);

    let customer_id = contextValue.customer_id ;
 */

export const SurveyType = {
    S360: 'S360',
    S175: 'S175'
} ;

export const APP_TIMEOUT_MINUTES = 60 ;
export const TIMEOUT = ( 60 * 1000 * APP_TIMEOUT_MINUTES );

export const ConfigOptions = {
    HEADER_NAME : 1,
    HOME_PAGE_TITLE : 2,
    SECTION_PAGE_TITLE: 3,
    HEADER_LOGO_PATH: 4,
    LOGIN_PAGE_LOGO_PATH: 5,
    PRIVACY_HEADER_TITLE: 6,
    PRIVACY_SUB_TITLE: 7,
    DASHBOARD_TITLE_COMPLETION: 8,
    DASHBOARD_TITLE_COMPLETION_ADMIN: 9,
    GRAPH_INTERVAL: 10,
    PDF_PARAMETERS: 11,
}

export const ValidSurveyTypes = [ SurveyType.S175, SurveyType.S360 ];

const config = {
    [ SurveyType.S175 ]:
        {
            [ ConfigOptions.GRAPH_INTERVAL ]: 2,
            [ ConfigOptions.HEADER_NAME ]: 'Safeguarding',
            [ ConfigOptions.HOME_PAGE_TITLE ]: 'S175 Safeguarding Review',
            [ ConfigOptions.SECTION_PAGE_TITLE ]: 'Safeguarding Standards',
            [ ConfigOptions.HEADER_LOGO_PATH ]: '/app/style/image/large_ncasp_logo.png',
            [ ConfigOptions.LOGIN_PAGE_LOGO_PATH ]: "/app/style/image/ncasp_logo.png",
            [ ConfigOptions.PRIVACY_HEADER_TITLE ]: 'S175 Audit Acceptable Use Policy',
            [ ConfigOptions.PRIVACY_SUB_TITLE ]: 'Welcome to the S175 Safeguarding System',
            [ ConfigOptions.DASHBOARD_TITLE_COMPLETION ]: 'Completed Questions by Standard',
            [ ConfigOptions.DASHBOARD_TITLE_COMPLETION_ADMIN ]: 'Completed Audits RAG Status By Standard - All Schools',
            [ ConfigOptions.PDF_PARAMETERS ]: {logoPath: '/app/style/image/ncasp_logo.png', imageWidth: 300, imageHeight: 300, text: 'Safeguarding Full Report', actionText: 'Safeguarding Actions Report' },
        },
    [ SurveyType.S360 ]:
        {
            [ ConfigOptions.GRAPH_INTERVAL ]: 5,
            [ ConfigOptions.HEADER_NAME ]: 'Online Safety Audit',
            [ ConfigOptions.HOME_PAGE_TITLE ]: 'Online Safety Audit',
            [ ConfigOptions.SECTION_PAGE_TITLE ]: 'Safety Review Key Areas',
            [ ConfigOptions.HEADER_LOGO_PATH ]: '/app/style/image/Northumberland_ICT_Team_large_logo.png',
            [ ConfigOptions.LOGIN_PAGE_LOGO_PATH ]: '/app/style/image/Northumberland_ICT_Login_logo.png',
            [ ConfigOptions.PRIVACY_HEADER_TITLE ]: 'Northumberland ICT & eLearning Acceptable Use Policy',
            [ ConfigOptions.PRIVACY_SUB_TITLE ]: 'Welcome to the Online Safety Audit System',
            [ ConfigOptions.DASHBOARD_TITLE_COMPLETION ]: 'Completed Questions by Key Area',
            [ ConfigOptions.DASHBOARD_TITLE_COMPLETION_ADMIN ]: 'Completed Audits RAG Status By Key Area - All Schools',
            [ ConfigOptions.PDF_PARAMETERS ]: {logoPath: '/app/style/image/Northumberland_ICT_Team_logo.png', imageWidth: 1000, imageHeight: 250, text:'Online Safety Full Report', actionText: 'Online Safety Actions Report'},
        },
}

const GlobalProvider = ( {dataset, children} ) =>
{
    const onIdle = () => {
        window.location.replace(dataset.logout_script + '&reason=timeout' );
    }

    const onActive = (event) => {
        console.log ( "Active!!") ;
    }

    const { start } = useIdleTimer({
        timeout: TIMEOUT,
        startManually: true,
        onIdle,
        onActive });

    const [ currentSurveyType, setCurrentSurveyType ] = useState ( dataset.survey ) ;

    useEffect(() => {
        start()
    }, []);

    const getCustomObject = ( customObjectId ) =>
    {
        if ( ValidSurveyTypes.includes ( currentSurveyType ))
        {
            if ( config [ currentSurveyType ] !== undefined )
            {
                if ( config [ currentSurveyType ][customObjectId] !== undefined )
                {
                    return config [ currentSurveyType ][customObjectId] ;
                }
                else
                {
                    throw new Error("Missing Object definition in config") ;
                }
            }
            else
            {
                throw new Error("Missing survey type definition in config") ;
            }
        }
        else
        {
            throw new Error("Invalid Survey Type") ;
        }
    }

    const getMobileSectionIcon = ( section, section_ref ) =>
    {

        switch ( currentSurveyType )
        {
            case SurveyType.S175:
                return 'S' + section;
            case SurveyType.S360:
                return section_ref;
            default:
                throw new Error ( "Invalid section icon for this Survey Type" );
        }
    }

    const getHomePageIntro = () =>
    {
        switch ( currentSurveyType )
        {
            case SurveyType.S175:
                return <Fragment>
                    <p>Standard 175 of the Education Act 2002 was implemented in June 2004. This introduced new statutory duties for schools, governing bodies and local authorities, including the need to provide information to the local authority about how the duties set out in the DfE statutory guidance Keeping Children Safe in Education have been discharged. Independent schools and Academies are covered under Standard 157 of the same Act.</p>
                    <p>Northumberland's S175 Safeguarding Standards Audit is unique to our county and reflects the safeguarding context that we are working in with children. It also captures the safeguarding priorities of the Northumberland Children and Adult's Safeguarding Partnership (NCASP).<b> As stated in the statutory guidance, settings are required to comply with the arrangements of their local safeguarding partnership so that safeguarding standards can be monitored effectively.</b></p>
                    <p>While we are aware that schools may commission safeguarding support from external providers, we would stress the value in completing the NCC/NCASP S175, which evidences compliance with local priorities and can be used during Ofsted visits to evidence strong partnership working. When investigating Ofsted Complaints Against Schools, we are required to report back to Ofsted on whether the school has completed and submitted their S175 audit.</p>
                    <p>This audit is a working document which will be reviewed once a term by the Schools’ Safeguarding team on behalf of Northumberland Children’s and Adult’s Safeguarding partnership (NCASP). The S175 must be completed by <strong>Friday 6th November</strong> and it should then be reviewed and updated regularly by the lead DSL.</p>
                    <p>Section 10 has been rewritten this year to reflect the ongoing priorities of the local authority. This means that responses from last year will not be carried over but we hope that this change will provide a better overview of children who have, or have had, a social worker and those in kinship care. </p>
                    <p>Responses to the question about Young Carers will be shared with the Young Carers Network. If you do not wish your school's response to this question to be shared, please contact a member of the Schools Safeguarding Team by <strong>Friday 6th November</strong>.</p>
                    </Fragment>
            case SurveyType.S360:
                return <Fragment>
                    <p>As stated in the Keeping Children Safe in Education document, <span
                        style={ { fontWeight: 'bold' } }> "Schools and colleges should consider carrying out an annual review of their approach to online safety,
                    </span> supported by an annual risk assessment that considers and reflects the risks their children face."</p>

                    <p>This audit is designed to take you through six key areas of online safety in order to identify areas of strength and areas for
                        future development. We suggest that you use this tool to carry out a scheduled formal review at least annually, but encourage
                        you to use the notes and actions field as a living document to reflect updates and changes as they occur throughout the
                        year.</p>
                    <ul>
                        <li><p>Consider tackling one category at a time, especially if this is your first audit which can be quite time consuming.</p>
                        </li>
                        <li><p>Take a look at the new ‘Technical annexe’ and 'Mapping’ documents under guidance. These can be downloaded and shared
                            with your technician, they link to the DfE Digital standards.</p></li>
                        <li><p>Upload any evidence to the documents section.</p></li>
                        <li><p>Once all categories are complete, remember to click the blue <span
                            style={ { fontWeight: 'bold', color: 'var(--s360-icon-complete)' } }>'SUBMIT' </span>button located on the ‘Key areas’
                            page.
                        </p></li>
                    </ul>
                </Fragment>
            default:
                throw new Error ( "Invalid page intro for this Survey Type") ;
        }
    }
    const getNotSubmittedText = (yr) =>
    {
        const academicYearRangeStr = yr + ' - ' + (yr+1) ;

        switch ( currentSurveyType )
        {
            case SurveyType.S175:
                return <Fragment>
                    <p>This audit report is for the academic year {academicYearRangeStr}.
                        Please complete all of the sections below, providing evidence and actions where appropriate.
                        <span style={{color: '#1062A5'}}> When complete, you can submit this report by clicking the submit button below.</span></p>
                </Fragment>;
            case SurveyType.S360:
                return <Fragment>
                    <p>This audit is for the academic year {academicYearRangeStr}. Please complete all of the sections below, providing evidence and actions
                        where appropriate. This is a detailed and thorough audit, we recommend scheduling the completion of one area at a time, saving
                        progress as you go. <span style={ { color: 'darkcyan' } }> When all sections are complete, you can submit this report by clicking the submit button below.</span></p>
                </Fragment>;
            default:
                throw new Error ( "Invalid page intro for this Survey Type" );
        }
    }

    const getEvaluateInfo = () =>
    {
        switch ( currentSurveyType )
        {
            case SurveyType.S175:
                return <Fragment>
                        <p>Supporting Evidence should tell us how are you evidencing your compliance. Evaluate your situation as: <span style={{color: 'var(--s360-icon-green)'}}> Green - Fully Compliant,</span><span style={{color: 'var(--s360-icon-orange)'}}> Amber - Partially Met,</span><span style={{color:'var(--s360-safe-icon-red)'}}> Red - Not Compliant</span>.
                            Questions rated red or amber should have an <span style={{fontWeight:'bold'}}>action identified,</span><span style={{fontWeight:'bold'}}> person named to complete</span> and a <span style={{fontWeight:'bold'}}>date for completion.</span></p>
                        </Fragment>;
            case SurveyType.S360:
                return <Fragment>
                        <p>Supporting Evidence should tell us how are you evidencing your compliance. If you evaluate your situation as <span style={{color: 'var(--s360-icon-green)'}}> GREEN </span> you are fully compliant. If you evaluate your situation as <span style={{color: 'var(--s360-icon-orange)'}}>AMBER</span> or <span style={{color:'var(--s360-safe-icon-red)'}}>RED</span> you must identify an action to comply with safety standards if no suggested action is displayed when you select amber or red.</p>
                        </Fragment>
            default:
                throw new Error("Invalid evaluate info for this Survey Type") ;
        }
    }

    const unNull = (value) =>
    {
        return value === 'null' ? null : value ;
    }

    const isFeatureEnabled = ( name ) =>
    {
        const features = JSON.parse(dataset.features) ;

        if ( features[name] === true )
        {
            return true ;
        }
        else
        {
            return false ;
        }
    }

    const system_params = () =>
    {
        const auth = dataset.authority_id ;

        switch ( auth )
        {
            case 'sun':
                return (
                    {
                        authority_id: unNull ( dataset.authority_id ),
                        authority_logo_path:
                            {
                                style: {
                                    height: '75px',
                                    width: '150px',
                                    marginTop: '15px'
                                },
                                imagePath: '/app/style/image/' + auth + '/tff.png',
                            },
                    }
                )
            default:
                return (
                    {
                        authority_id: unNull(dataset.authority_id),
                        authority_logo_path:
                            {
                                style: {
                                    height: '100px',
                                    width: '200px',
                                    marginTop: '5px'
                                },
                                imagePath: '/app/style/image/' + auth + '/ncc-logo.svg',
                            },
                    }
                )
        }
    }

    return (
        <GlobalContext.Provider
            value={{
                user_type: +(dataset.user_type),
                action: unNull(dataset.action),
                reason: unNull(dataset.reason),
                authority_id: unNull(dataset.authority_id),
                system_params: system_params(),
                username: unNull(dataset.username),
                token: unNull(dataset.token),
                current_academic_year: +(dataset.academic_year),
                isFeatureEnabled: ( feature_name ) =>
                {
                    return isFeatureEnabled ( feature_name ) ;
                },
                getCustomObject: ( objectId ) =>
                {
                    return getCustomObject( objectId ) ;
                },
                getHomePageIntro: () =>
                {
                    return getHomePageIntro() ;
                },
                getNotSubmittedText: (yr) =>
                {
                   return getNotSubmittedText (yr);
                },
                getEvaluateInfo: () =>
                {
                    return getEvaluateInfo() ;
                },
                getCurrentSurveyType: () =>
                {
                    return currentSurveyType;
                },
                getMobileSectionIcon: (section, icon_filename) =>
                {
                    return getMobileSectionIcon(section, icon_filename);
                },
            }}
        >
        {children}
        </GlobalContext.Provider>
    )
}

export default GlobalProvider;